import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import CurrencyFormat from "react-currency-format";
import cardsService from "../../services/cards.services";
import clientmanagmentObj from "../../services/client-managment.service";
import depositService from "../../services/deposit.service";
import transactionService from "../../services/transaction.service";
import useCountUp from "../../helpers/useCountUp";

/**
 * Home — an operations command centre.
 *
 * A NEW surface, deliberately separate from SuperAdminDashboard so none of
 * that component's logic is disturbed. Everything here reads from services
 * the app already exposes:
 *
 *   cardsService.getCardStatusData       -> { Active, InActive }
 *   clientmanagmentObj.getclientcount    -> { totalClient, activeClient, ... }
 *   depositService.getDepositCounts      -> { totalCount }
 *   clientmanagmentObj.getChangePinCount -> { totalCount }
 *   transactionService.getTransactionStatsData
 *                      -> { totalAmount, totalUsers, averageTransactions }
 *   clientmanagmentObj.searchLogs        -> [{ timestamp, method, endpoint,
 *                                              statusCode, message, ... }]
 *
 * No new endpoint, no mutation, read-only. The activity feed refreshes only
 * when the operator asks — no background polling, so an open tab never
 * generates traffic on its own.
 *
 * Motion runs once on mount and settles; nothing loops.
 */

/* a headline figure with a rolling counter */
const Stat = ({ label, value, tone, delay, prefix, format }) => {
  const shown = useCountUp(value ?? 0);
  return (
    <div
      className={"bopay-home-stat" + (tone ? " bopay-home-stat--" + tone : "")}
      style={{ animationDelay: delay + "ms" }}
    >
      <p className="bopay-home-stat-label">{label}</p>
      <p className="bopay-home-stat-value">
        {value === null ? (
          <span className="bopay-home-dash">—</span>
        ) : format === "currency" ? (
          <Fragment>
            {prefix ? <span className="bopay-home-stat-pre">{prefix}</span> : null}
            <CurrencyFormat
              value={shown}
              displayType={"text"}
              thousandSeparator={true}
            />
          </Fragment>
        ) : (
          shown.toLocaleString()
        )}
      </p>
    </div>
  );
};

/* status distribution as a stacked bar — reads correctly at extreme ratios
   where a donut's minority arc becomes an invisible sliver */
const StatusBar = ({ active, inactive }) => {
  const total = active + inactive;
  const pct = total > 0 ? (active / total) * 100 : 0;
  return (
    <div className="bopay-home-bar-wrap">
      <div className="bopay-home-bar-head">
        <span className="bopay-home-bar-pct">{pct.toFixed(1)}%</span>
        <span className="bopay-home-bar-cap">of cards are active</span>
      </div>
      <div className="bopay-home-bar" role="img"
           aria-label={pct.toFixed(1) + "% of cards active"}>
        <span className="bopay-home-bar-fill" style={{ width: pct + "%" }} />
      </div>
      <ul className="bopay-home-legend">
        <li>
          <span className="bopay-home-swatch" style={{ background: "#89455e" }} />
          Active
          <strong>{active.toLocaleString()}</strong>
        </li>
        <li>
          <span className="bopay-home-swatch bopay-home-swatch--dim" />
          Inactive
          <strong>{inactive.toLocaleString()}</strong>
        </li>
      </ul>
    </div>
  );
};

const relativeTime = (ts) => {
  if (!ts) return "";
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return secs + "s ago";
  const mins = Math.round(secs / 60);
  if (mins < 60) return mins + "m ago";
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + "h ago";
  return Math.round(hrs / 24) + "d ago";
};

const Home = () => {
  const { user: currentUser } = useSelector((state) => state.auth);

  const [cards, setCards] = useState(null);
  const [clients, setClients] = useState(null);
  const [txnStats, setTxnStats] = useState(null);
  const [depositCount, setDepositCount] = useState(null);
  const [pinCount, setPinCount] = useState(null);
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [now, setNow] = useState(new Date());

  const clientId = currentUser?.role?.includes("Client admin")
    ? currentUser?.user?.ClientId
    : "";

  // quiet clock — one tick a minute, not a second
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const loadLogs = useCallback(() => {
    setLogsLoading(true);
    // Page size 50 matches LogRecords.js. An earlier version asked for 8 and
    // came back empty — the endpoint appears to expect the standard page
    // size, so request that and slice client-side instead.
    clientmanagmentObj
      .searchLogs(null, null, null, 1, 50, null)
      .then((resp) => {
        const rows = Array.isArray(resp?.data)
          ? resp.data
          : resp?.data?.result || resp?.data?.payload || [];
        setLogs(Array.isArray(rows) ? rows.slice(0, 8) : []);
        setLogsLoading(false);
      })
      .catch(() => {
        setLogs([]);
        setLogsLoading(false);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const safe = (fn) => (v) => { if (!cancelled) fn(v); };

    cardsService
      .getCardStatusData(clientId)
      .then(safe((r) => setCards(r?.data?.payload?.result ?? {})))
      .catch(safe(() => setCards({})));

    clientmanagmentObj
      .getclientcount()
      .then(safe((r) => setClients(r?.data?.payload?.result ?? {})))
      .catch(safe(() => setClients({})));

    transactionService
      .getTransactionStatsData(clientId)
      .then(safe((r) => setTxnStats(r?.data?.payload?.result ?? {})))
      .catch(safe(() => setTxnStats({})));

    depositService
      .getDepositCounts("", "Pending", "0001-01-01", "0001-01-01", "0001-01-01", "0001-01-01", "", 1, "")
      .then(safe((r) => setDepositCount(r?.data?.payload?.result?.totalCount ?? 0)))
      .catch(safe(() => setDepositCount(0)));

    clientmanagmentObj
      .getChangePinCount("", "", "0001-01-01T00:00:00.000Z", "0001-01-01T00:00:00.000Z", "", 1)
      .then(safe((r) => setPinCount(r?.data?.payload?.result?.totalCount ?? 0)))
      .catch(safe(() => setPinCount(0)));

    loadLogs();

    return () => { cancelled = true; };
  }, [clientId, loadLogs]);

  const greeting = () => {
    const h = now.getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  };

  const firstName = (currentUser?.user?.Name || "").split(" ")[0];
  const active = Number(cards?.Active ?? 0);
  const inactive = Number(cards?.InActive ?? 0);
  const totalCards = active + inactive;

  // health derived from the same log page we already fetched — no extra call
  const okCount = (logs || []).filter(
    (l) => Number(l?.statusCode) >= 200 && Number(l?.statusCode) < 400
  ).length;
  const health = logs && logs.length > 0 ? (okCount / logs.length) * 100 : null;
  const failures = logs ? logs.length - okCount : 0;

  const queue = [
    { key: "dep", label: "Deposits awaiting approval", count: depositCount, to: "/deposit" },
    { key: "pin", label: "PIN change requests", count: pinCount, to: "/changepins" },
  ].filter((q) => q.count !== null && q.count > 0);

  const loadingQueue = depositCount === null && pinCount === null;

  return (
    <Fragment>
      <div className="col-xl-9 col-lg-12 bo-main-content bopay-home">
        {/* hero */}
        <section className="bopay-home-hero">
          <div className="bopay-home-mesh" aria-hidden="true">
            <span></span><span></span><span></span>
          </div>
          <div className="bopay-home-hero-inner">
            <p className="bopay-home-eyebrow">Super Admin · Operations</p>
            <h1 className="bopay-home-greeting">
              {greeting()}{firstName ? `, ${firstName}.` : "."}
            </h1>
            <p className="bopay-home-clock">
              {now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
              <span className="bopay-home-dot" aria-hidden="true"></span>
              {now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
        </section>

        {/* Quick actions sit directly under the hero: these are the places an
            operator jumps to, so they belong where the hand reaches first
            rather than below the figures. */}
        <section className="bopay-home-actions">
          {[
            { to: "/cards", label: "Cards" },
            { to: "/clients", label: "Clients" },
            { to: "/deposit", label: "Deposits" },
            { to: "/logrecords", label: "Log records" },
          ].map((a, i) => (
            /* 40ms steps, ahead of the stat tiles below (which start at 0 and
               step 70ms) so the page reveals top-down in reading order */
            <Link key={a.to} to={a.to} className="bopay-home-action"
                  style={{ animationDelay: i * 40 + "ms" }}>
              {a.label}
              <span className="bopay-home-action-go" aria-hidden="true"></span>
            </Link>
          ))}
        </section>

        {/* money */}
        <section className="bopay-home-stats">
          <Stat label="Transaction volume"
                value={txnStats ? Number(txnStats?.totalAmount ?? 0) : null}
                format="currency" tone="good" delay={160} />
          <Stat label="Avg. transactions"
                value={txnStats ? Number(txnStats?.averageTransactions ?? 0) : null}
                delay={230} />
          <Stat label="Total cards"
                value={cards ? totalCards : null} delay={300} />
          <Stat label="Active cards"
                value={cards ? active : null} tone="good" delay={370} />
        </section>

        {/* clients — shown as one composed figure rather than two tiles.
            totalClient and activeClient are frequently identical (the API's
            inActiveClient means SUSPENDED, not "not active"), so two separate
            tiles reading 247 / 247 looked like a bug. One tile with the
            suspended count beside it states the same data unambiguously. */}
        <section className="bopay-home-stats">
          <div className="bopay-home-stat" style={{ animationDelay: "160ms" }}>
            <p className="bopay-home-stat-label">Clients</p>
            <p className="bopay-home-stat-value">
              {clients === null
                ? <span className="bopay-home-dash">—</span>
                : Number(clients?.totalClient ?? 0).toLocaleString()}
            </p>
            {clients !== null && (
              <p className="bopay-home-stat-sub">
                {Number(clients?.inActiveClient ?? 0) > 0
                  ? `${Number(clients.inActiveClient).toLocaleString()} suspended`
                  : "None suspended"}
              </p>
            )}
          </div>
          <Stat label="Inactive cards"
                value={cards ? inactive : null} delay={230} />
          <Stat label="Transacting users"
                value={txnStats ? Number(txnStats?.totalUsers ?? 0) : null}
                delay={300} />
          <Stat label="Pending deposits"
                value={depositCount} tone="good" delay={370} />
        </section>

        {/* status + queue */}
        <section className="bopay-home-split">
          <div className="bopay-home-panel">
            <div className="bopay-home-panel-head">
              <h2 className="bopay-home-panel-title">Card status</h2>
              <span className="bopay-chip">Live</span>
            </div>
            {cards ? (
              <StatusBar active={active} inactive={inactive} />
            ) : (
              <div className="bopay-home-skeleton bopay-home-skeleton--chart" />
            )}
          </div>

          <div className="bopay-home-panel">
            <div className="bopay-home-panel-head">
              <h2 className="bopay-home-panel-title">Needs attention</h2>
            </div>

            {loadingQueue && <div className="bopay-home-skeleton" />}

            {!loadingQueue && queue.length === 0 && (
              <p className="bopay-home-clear">
                <span className="bopay-home-clear-dot" aria-hidden="true"></span>
                All queues are clear.
              </p>
            )}

            {!loadingQueue && queue.map((q, i) => (
              <Link key={q.key} to={q.to} className="bopay-home-queue"
                    style={{ animationDelay: 120 + i * 90 + "ms" }}>
                <span className="bopay-home-queue-count">
                  {q.count}
                  <span className="bopay-home-pulse" aria-hidden="true"></span>
                </span>
                <span className="bopay-home-queue-label">{q.label}</span>
                <span className="bopay-home-queue-go" aria-hidden="true"></span>
              </Link>
            ))}
          </div>
        </section>

        {/* platform activity */}
        <section className="bopay-home-panel bopay-home-feed-panel">
          <div className="bopay-home-panel-head">
            <h2 className="bopay-home-panel-title">Platform activity</h2>
            <div className="bopay-home-feed-tools">
              {health !== null && (
                <span className={"bopay-home-health" + (failures > 0 ? " bopay-home-health--warn" : "")}>
                  <span className="bopay-home-health-bar" aria-hidden="true">
                    <span style={{ width: health + "%" }} />
                  </span>
                  {health.toFixed(0)}% OK
                  {failures > 0 ? ` · ${failures} failing` : ""}
                </span>
              )}
              <button type="button" className="bopay-home-refresh"
                      onClick={loadLogs} disabled={logsLoading}
                      title="Refresh activity">
                <span className={"bopay-home-refresh-icon" + (logsLoading ? " is-spinning" : "")}
                      aria-hidden="true"></span>
                Refresh
              </button>
            </div>
          </div>

          {logs === null && <div className="bopay-home-skeleton bopay-home-skeleton--feed" />}

          {logs && logs.length === 0 && (
            <p className="bopay-home-clear">
              <span className="bopay-home-clear-dot" aria-hidden="true"></span>
              No recent activity.
            </p>
          )}

          {logs && logs.length > 0 && (
            <ul className="bopay-home-feed">
              {logs.map((l, i) => {
                const code = Number(l?.statusCode);
                const tone = code >= 200 && code < 400 ? "good" : code ? "bad" : "muted";
                return (
                  <li key={i} className="bopay-home-feed-row"
                      style={{ animationDelay: 60 + i * 45 + "ms" }}>
                    <span className={"bopay-home-code bopay-home-code--" + tone}>
                      {l?.statusCode ?? "—"}
                    </span>
                    <span className="bopay-home-method">{l?.method ?? ""}</span>
                    <span className="bopay-home-endpoint" title={l?.endpoint}>
                      {l?.endpoint ?? "—"}
                    </span>
                    <span className="bopay-home-ago">{relativeTime(l?.timestamp)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

      </div>
    </Fragment>
  );
};

export default Home;
